import { useState, useEffect, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import { apiGet, errorMessage } from "./api";
import { PLANS } from "./plans";
import "./Invoices.css";

function formatDate(ts) {
  if (!ts) return "-";
  return new Date(ts * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Statuts Stripe traduits pour l'affichage ; un statut inconnu reste tel quel
const STATUS_LABELS = {
  paid: "Payée",
  open: "En attente",
  draft: "Brouillon",
  void: "Annulée",
  uncollectible: "Irrécouvrable",
};

function Invoices({ user, onNavigate, onLogout }) {
  const [invoices, setInvoices] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [billingFilter, setBillingFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");

  useEffect(() => {
    apiGet("/api/organizations").then(setOrgs).catch(() => setOrgs([]));
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFrom) params.append("from", dateFrom);
    if (dateTo) params.append("to", dateTo);
    if (orgFilter) params.append("organization_id", orgFilter);
    if (billingFilter) params.append("interval", billingFilter);
    if (planFilter) params.append("plan_id", planFilter);
    try {
      const data = await apiGet(`/api/billing/admin/invoices?${params}`,
        "Impossible de charger les factures");
      setInvoices(data.invoices);
      setError("");
    } catch (e) {
      setInvoices([]);
      setError(errorMessage(e, "Impossible de charger les factures"));
    }
    setLoading(false);
  }, [dateFrom, dateTo, orgFilter, billingFilter, planFilter]);

  // Léger debounce : les filtres date se saisissent en plusieurs interactions
  useEffect(() => {
    const timer = setTimeout(fetchInvoices, 250);
    return () => clearTimeout(timer);
  }, [fetchInvoices]);

  const total = invoices.reduce((sum, inv) => sum + (inv.status === "paid" ? inv.total : 0), 0);

  return (
    <div className="fi-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Factures"
        onBack={() => onNavigate("admin")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      >
        <button className="rc-btn rc-btn-outline site-header-btn" onClick={fetchInvoices}>
          Rafraîchir
        </button>
      </GestionHeader>

      <main className="fi-main">
        <div className="fi-filters">
          <label>
            Émises du
            <input type="date" value={dateFrom}
                   onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label>
            au
            <input type="date" value={dateTo}
                   onChange={(e) => setDateTo(e.target.value)} />
          </label>
          <label>
            Client
            <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
              <option value="">Tous</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </label>
          <label>
            Abonnement
            <select value={billingFilter} onChange={(e) => setBillingFilter(e.target.value)}>
              <option value="">Tous</option>
              <option value="monthly">Mensuel</option>
              <option value="annual">Annuel</option>
            </select>
          </label>
          <label>
            Offre
            <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              <option value="">Toutes</option>
              {PLANS.map((p) => (
                <option key={p.id} value={p.id}>{p.tier}</option>
              ))}
            </select>
          </label>
        </div>

        {error && <div className="fi-error">{error}</div>}

        {loading ? (
          <p className="fi-empty">Chargement...</p>
        ) : invoices.length === 0 ? (
          <p className="fi-empty">Aucune facture ne correspond à ces critères.</p>
        ) : (
          <>
            <p className="fi-summary">
              {invoices.length} facture{invoices.length > 1 ? "s" : ""} ·{" "}
              {total.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € encaissés
            </p>
            <div className="fi-table-scroll">
              <table className="fi-table">
                <thead>
                  <tr>
                    <th>Émise le</th>
                    <th>Numéro</th>
                    <th>Client</th>
                    <th>Offre</th>
                    <th>Abonnement</th>
                    <th className="fi-num">Montant</th>
                    <th>Statut</th>
                    <th>
                      <span className="fi-sr-only">Documents</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.number || inv.created}>
                      <td>{formatDate(inv.created)}</td>
                      <td className="fi-mono">{inv.number || "—"}</td>
                      <td>
                        <span className="fi-org">{inv.organization || "—"}</span>
                        <span className="fi-email">{inv.customer_email}</span>
                      </td>
                      <td>{inv.plan_label || "—"}</td>
                      <td>
                        {inv.billing === "monthly" ? "Mensuel"
                          : inv.billing === "annual" ? "Annuel" : "—"}
                      </td>
                      <td className="fi-num">
                        {inv.total.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} {inv.currency === "EUR" ? "€" : inv.currency}
                      </td>
                      <td>
                        <span className={`fi-status fi-status-${inv.status}`}>
                          {STATUS_LABELS[inv.status] || inv.status}
                        </span>
                      </td>
                      <td className="fi-links">
                        {inv.pdf && (
                          <a href={inv.pdf} target="_blank" rel="noopener noreferrer">PDF</a>
                        )}
                        {inv.url && (
                          <a href={inv.url} target="_blank" rel="noopener noreferrer">Voir</a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default Invoices;
